import { StatusBadge } from "@/components/status-badge";
import { OrderItemsDialog } from "@/components/tables/_components/order-items-dialog";
import { OrderStatusDialog } from "@/components/tables/_components/order-status-dialog";
import { parseSkuMatchResults } from "@/components/tables/sku-matches-dropdown";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMS365Status } from "@/hooks/use-ms365-status";
import { useZohoStatus } from "@/hooks/use-zoho-status";
import { MatchersService } from "@/services/matchers-service";
import { OrderService } from "@/services/order-services";
import { ProductService } from "@/services/product-service";
import type { Order } from "@/services/types/orders";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  notFound,
  useCanGoBack,
  useRouter,
} from "@tanstack/react-router";
import {
  AlertTriangle,
  ChevronLeft,
  Loader2,
  Pencil,
  Truck,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import OrderTabContent from "./-components/order-tabs-content";

export const Route = createFileRoute("/_main/orders/$orderId")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const order = await OrderService.getOrderById(params.orderId);

    if (!order) {
      throw notFound();
    }

    return order;
  },
  notFoundComponent: () => {
    return (
      <div className="flex min-h-screen items-center justify-center bg-soft-gray p-4">
        <div className="w-full max-w-md space-y-6 rounded-xl bg-white p-8 text-center">
          <AlertTriangle
            className="mx-auto mb-4 h-16 w-16 text-neutral-gray"
            strokeWidth={1.5}
          />
          <h1 className="mb-2 font-bold text-3xl text-dark-purple">
            Order Not Found
          </h1>
          <p className="mb-4 text-cool-gray">
            We couldn't locate the order details you're looking for. The order
            might have been deleted or the link is incorrect.
          </p>
          <Link
            to="/orders"
            className="inline-block rounded-lg bg-soft-purple px-6 py-3 text-dark-purple transition-colors hover:bg-soft-purple/90"
          >
            Return to Orders
          </Link>
        </div>
      </div>
    );
  },
});

function RouteComponent() {
  const { orderId } = Route.useParams();
  const [activeTab, setActiveTab] = useState("order_details");
  const ordr = Route.useLoaderData();
  const [currentOrder, setCurrentOrder] = useState(ordr);

  const router = useRouter();
  const queryClient = useQueryClient();
  const canGoBack = useCanGoBack();
  const { status: zohoStatus } = useZohoStatus();
  const { status: ms365Status } = useMS365Status();

  const hasERPIntegration =
    Boolean(zohoStatus?.connected) || Boolean(ms365Status?.connected);

  if (!ordr) {
    router.navigate({ to: "/orders" });
  }

  useEffect(() => {
    setCurrentOrder(ordr);
  }, [ordr]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const updateOrder = async (order_id: number, ord: Order): Promise<void> => {
    const clone = { ...ord };
    setCurrentOrder(clone);
    await OrderService.updateOrderPut(order_id, ord);
  };

  const { data: skuMatchData, isPending: isSkuMatchesPending } = useQuery({
    queryKey: ["sku-match-results", currentOrder.id],
    queryFn: () => MatchersService.getSkuMatchResults(currentOrder.id),
    enabled: !!currentOrder.id,
    staleTime: 60_000,
  });

  const skuMatchesByIndex = useMemo(
    () => parseSkuMatchResults(skuMatchData),
    [skuMatchData],
  );

  const { data: allInventoryItems, isPending: isInventoryListPending } =
    useQuery({
      queryKey: ["inventory_products"],
      queryFn: () => ProductService.list({ pageSize: 1000 }),
    });

  const erpOrderIdLabel = currentOrder.erp_order_id ?? "Not sent";
  const orderMetaTypography = {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "24px",
    lineHeight: "140%",
    letterSpacing: "0px",
  };

  const { mutate: pushOrderToERP, isPending: isERPCreating } = useMutation({
    mutationKey: ["push-order-to-erp", currentOrder.id],
    mutationFn: () =>
      OrderService.pushOrderToERPAndWaitForERPId(currentOrder.id),
    onSuccess: (erpOrderId) => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });

      if (erpOrderId) {
        setCurrentOrder((previousOrder) => ({
          ...previousOrder,
          erp_order_id: erpOrderId,
        }));
        toast.success("Order synced to ERP");
        return;
      }

      toast.success("Order queued for ERP sync");
      void router.invalidate();
    },
    onError: () => {
      toast.error("Failed to queue order for ERP sync");
    },
  });

  return (
    <div>
      <Button
        variant="link"
        className="mt-8 ml-4"
        onClick={() => router.history.back()}
        disabled={!canGoBack}
      >
        <ChevronLeft />
        Go back
      </Button>

      <div className="max-w-7xl p-4">
        <div className="mb-8 space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-foreground" style={orderMetaTypography}>
              <span className="font-semibold">Order ID:</span>{" "}
              <span className="font-normal">{currentOrder.id}</span>
              <span className="px-2 font-normal">|</span>
              <span className="font-semibold">ERP ID:</span>{" "}
              <span className="font-normal">{erpOrderIdLabel}</span>
            </p>
            <StatusBadge
              status={currentOrder.status}
              variant={
                currentOrder.status === "Pending"
                  ? "pending"
                  : currentOrder.status === "Approved"
                    ? "success"
                    : "error"
              }
            />
          </div>
        </div>

        <Tabs
          defaultValue="order_details"
          value={activeTab}
          onValueChange={handleTabChange}
          className="custom-tabs w-full"
        >
          <div className="flex flex-col md:flex-row md:justify-between">
            <TabsList className="mb-4 flex w-full justify-start overflow-x-auto border-gray-200 border-b bg-transparent md:mb-0 md:w-auto">
              <TabsTrigger
                value="order_details"
                className="!rounded-none relative border-b-2 border-b-transparent px-4 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
              >
                Order Details
              </TabsTrigger>
              <TabsTrigger
                value="customer_info"
                className="!rounded-none relative border-b-2 border-b-transparent px-4 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                disabled={true}
              >
                Customer Info
              </TabsTrigger>
              <TabsTrigger
                value="delivery"
                className="!rounded-none relative border-b-2 border-b-transparent px-4 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none"
                disabled={true}
              >
                Delivery
              </TabsTrigger>
            </TabsList>
            {currentOrder.status === "Pending" && (
              <div className="mt-4 flex gap-2 md:mt-0">
                <OrderItemsDialog
                  order={currentOrder}
                  onSave={updateOrder}
                  allInventoryItems={allInventoryItems!}
                  skuMatchesByIndex={skuMatchesByIndex}
                  updateFunction={updateOrder}
                  trigger={
                    <Button
                      variant="outline"
                      disabled={currentOrder.status !== "Pending"}
                      className="flex items-center gap-2"
                    >
                      <Pencil className="h-4 w-4" />
                      <span>Edit</span>
                    </Button>
                  }
                />
                <OrderStatusDialog
                  order={currentOrder}
                  onSave={() =>
                    OrderService.updateOrderPut(currentOrder.id, currentOrder)
                  }
                  trigger={
                    <Button
                      variant="default"
                      disabled={currentOrder.status !== "Pending"}
                    >
                      Process Order
                      <Truck className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>
            )}
            {currentOrder.status === "Approved" && (
              <div className="mt-4 flex gap-2 md:mt-0">
                {isERPCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : currentOrder.erp_order_id == null ? (
                  <Button
                    onClick={() => pushOrderToERP()}
                    disabled={!hasERPIntegration}
                  >
                    Send to ERP
                  </Button>
                ) : (
                  <div className="rounded-md bg-green-100 px-2 py-0.5 text-center text-green-800 text-xs">
                    <div>ERP Order ID</div>
                    <span className="text-sm">{currentOrder.erp_order_id}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <TabsContent value="order_details" className="mt-6">
            <OrderTabContent
              activeTab="order_details"
              updateFunction={updateOrder}
              order={currentOrder}
            />
          </TabsContent>

          <TabsContent value="customer_info" className="mt-6">
            <OrderTabContent
              activeTab="customer_info"
              updateFunction={updateOrder}
              order={currentOrder}
            />
          </TabsContent>

          <TabsContent value="delivery" className="mt-6">
            <OrderTabContent
              activeTab="delivery"
              updateFunction={updateOrder}
              order={currentOrder}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
